import create from 'zustand'
import i18next from 'i18next'
import { labelSorter } from '../../../shared/sortUtils'
import { Country, GroupedCompanyWithLegalEntitiesAndCountries } from '../../../types'
import { CompanySettingsState, CompanySettingsTabs } from '../types'

const initialValues = {
  storeInitialized: false,
  companiesWithLegalEntities: [],
  companiesForSelection: [],
  companySelectionCurrentValue: [],
  activeTab: CompanySettingsTabs.appraisalSeason,
  selected: {
    companyId: '',
    companyName: '',
    country: '' as Country,
  },
}

const showCountryInSelection = (activeTab: string) =>
  [
    CompanySettingsTabs.appraisal,
    CompanySettingsTabs.requests,
    CompanySettingsTabs.pyramids,
    CompanySettingsTabs.ranking,
    CompanySettingsTabs.cultivatingTalents,
    CompanySettingsTabs.targetTemplate,
    CompanySettingsTabs.targetReports,
    CompanySettingsTabs.appraisalSeason,
  ].includes(activeTab as CompanySettingsTabs)

const sortCompanies = (companiesWithLegalEntities: GroupedCompanyWithLegalEntitiesAndCountries[]) => {
  return companiesWithLegalEntities
    .map((company) => {
      return { ...company, companyCountries: [...company.companyCountries].sort((c1, c2) => labelSorter(c1.country, c2.country)) }
    })
    .sort((c1, c2) => labelSorter(c1.companyName, c2.companyName))
}

const transformCompaniesForSelection = (companiesWithLegalEntities: GroupedCompanyWithLegalEntitiesAndCountries[], activeTab: string) => {
  const sortedCompanies = sortCompanies(companiesWithLegalEntities)

  const showCountry = showCountryInSelection(activeTab)

  return showCountry
    ? sortedCompanies.map((company) => ({
        label: company.companyName,
        value: `${company.companyId}`,
        children: Array.from(new Set(company.companyCountries.map((countryAndLegalEntity) => countryAndLegalEntity.country))).map(
          (country) => ({
            label: country,
            value: `${country}`,
          }),
        ),
      }))
    : sortedCompanies.map((company) => ({ label: company.companyName, value: `${company.companyId}` }))
}

export const useHRSettingsStore = create<CompanySettingsState>((set) => ({
  ...initialValues,

  initStore: (companiesWithLegalEntities: GroupedCompanyWithLegalEntitiesAndCountries[]) =>
    set((state) => {
      const sortedCompanies = sortCompanies(companiesWithLegalEntities)
      const companiesForSelection = transformCompaniesForSelection(companiesWithLegalEntities, state.activeTab)

      const companyId = state.selected.companyId || sortedCompanies[0].companyId
      const companyName = state.selected.companyName || sortedCompanies[0].companyName
      const country = state.selected.country || (sortedCompanies[0].companyCountries[0].country as Country)

      const selected = {
        companyId,
        companyName,
        country,
      }

      const companySelectionCurrentValue = !!showCountryInSelection(state.activeTab) ? [companyId, country] : [companyId]

      return {
        companiesWithLegalEntities,
        companiesForSelection,
        selected,
        companySelectionCurrentValue,
        storeInitialized: true,
      }
    }),

  setCompaniesWithLegalEntities: (companiesWithLegalEntities: GroupedCompanyWithLegalEntitiesAndCountries[]) =>
    set({ companiesWithLegalEntities }),

  setActiveTab: (activeTab: string) =>
    set((state) => {
      const companiesForSelection = transformCompaniesForSelection(state.companiesWithLegalEntities, activeTab)

      const companySelectionCurrentValue = !!showCountryInSelection(activeTab)
        ? [state.selected.companyId, state.selected.country]
        : [state.selected.companyId]

      return {
        activeTab,
        companiesForSelection,
        companySelectionCurrentValue,
      }
    }),

  setSelected: ({ companyId, country = '' as Country }: { companyId: string; country?: Country }) =>
    set((state) => {
      const companySelectionCurrentValue = !!country ? [companyId, country] : [companyId]

      const company = state.companiesWithLegalEntities.find((company) => company.companyId === companyId)

      if (!!company) {
        const _country = !!country
          ? country
          : company.companyCountries.map((el) => el.country as Country).some((el) => el === state.selected.country)
          ? state.selected.country
          : (company.companyCountries[0].country as Country)
        const companyName = company.companyName || i18next.t('companySettingsView.reviewerTab.addModal.headingNoCompany')
        return {
          companySelectionCurrentValue,
          selected: { companyId, companyName, country: _country },
        }
      }

      return { ...state }
    }),
}))
